import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs/operators';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AuthenticationService, IWebAppUser, WebAppUser, AccessLevelLabels, AccessLevel } from '@services/authentication.service';

/**
 * ProfileManageComponent
 */
@Component({
	selector: 'app-profile-manage',
	templateUrl: './profile-manage.component.html',
	styleUrls: ['./profile-manage.component.scss']
})
export class ProfileManageComponent implements OnDestroy {

	/** Table Columns */
	public userTableColumns = ["userName", "displayName", "email", "view"];
	/** DataSource for table */
	public userDataSource: MatTableDataSource<IWebAppUser>;

	/** Table Paginator */
	@ViewChild(MatPaginator) paginator: MatPaginator;
	/** Table Sort */
	@ViewChild(MatSort, { static: false }) matSort: MatSort;

	/** System Users */
	public appUsers: WebAppUser[];
	/** Selected GUID */
	public selectedGUID: string;
	/** Current User */
	public currentUser: WebAppUser;
	/** Filter String */
	private filterString: string = "";

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/**
	 * Default Constructor
	 * @param globalFunctions
	 * @param router
	 * @param authenticationService
	 * @param siteSubscriptions
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		private router: Router,
		private authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions

	) {

		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter(currentUser => !!currentUser)
		).subscribe({
			next: (currentUser) => {
				// Check we have a validated User
				if (!this.currentUser) {
					this.currentUser = <IWebAppUser>currentUser;
					this.getAllUsers()
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false); },
			//complete: () => { console.log("this.authenticationService.currentUser$.subscribe: Complete"); }
		});

		this.siteSubscriptions.refreshUserList$.pipe()
			.subscribe({
				next: (refresh) => {
					if (refresh) {
						this.getAllUsers()
						this.siteSubscriptions.RefreshUserList(false);
					}
				}
			})
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Get All Users
	 */
	getAllUsers() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.authenticationService.getAllUsers$({}).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (users) => {
				// Check we have a validated User
				// if (!this.userDataSource) {
				this.appUsers = <IWebAppUser[]>users;
				this.userDataSource = new MatTableDataSource(this.appUsers);
				this.userDataSource.paginator = this.paginator;
				/** Make sure we default to alias in order */
				this.userDataSource.sort = this.matSort;
				this.filterUsers();
				// }
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.getAllUsers$", false); }
		});
	}

	/**
	 * Select User to Manage
	 * @param GUID
	 */
	SelectUser(GUID: any) {
		this.selectedGUID = GUID;
		// this.router.navigate(['../manageusers/' + GUID]);
	}

	/**
	 * Key up event from the search input on HTML
	 * @param event HTMLInputElement
	 */
	applyFilter(value: string) {
		const filterValue = value.toLowerCase();
		this.filterUsers(filterValue);
	}

	/**
	 * Filter Users List
	 * @param filterValue
	 */
	filterUsers(filterValue: string = this.filterString) {
		try {

			this.userDataSource.filterPredicate = (data: IWebAppUser, filter: string) => {
				/** Build a string per row to filter and convert to lower case */
				const textToSearch = (data.Name + data.NickName + data.Email).toLowerCase() || '';
				return textToSearch.indexOf(filter) !== -1;
			};

			this.userDataSource.filter = filterValue.trim().toLowerCase();

			if (this.userDataSource.filteredData.length == 0) {
				this.globalFunctions.DisplaySnackBar({
					message: "No matches for '" + filterValue.trim().toLowerCase() + "'",
					type: SnackType.Alert,
					duration: 1000
				})
			}

			this.filterString = filterValue;

		}
		catch (error) { this.globalFunctions.HandleError(error, "Error filtering list"); }
	}

	/**
	 * Get Account Roles Label
	 * @param role
	 * @returns
	 */
	getSafeAccessLevelLabel(role: AccessLevel): string {
		return AccessLevelLabels[role] ?? 'Unknown Level';
	}
}
